import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LaunchIcon from '@mui/icons-material/Launch';
import { toast } from 'react-toastify';

interface BravePushSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
}

const getNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Detect browser type
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('brave') || (navigator as any).brave?.isBrave) {
    return { name: 'Brave', settingsUrl: 'brave://settings/privacy', icon: '🦁' };
  } else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return { name: 'Chrome', settingsUrl: 'chrome://settings/content/notifications', icon: '🌐' };
  } else if (userAgent.includes('firefox')) {
    return { name: 'Firefox', settingsUrl: 'about:preferences#privacy', icon: '🦊' };
  } else if (userAgent.includes('edg')) {
    return { name: 'Edge', settingsUrl: 'edge://settings/content/notifications', icon: '🌐' };
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    return { name: 'Safari', settingsUrl: '', icon: '🧭' };
  } else {
    return { name: 'Browser', settingsUrl: '', icon: '🌐' };
  }
};

const BravePushSettingsModal: React.FC<BravePushSettingsModalProps> = ({
  open,
  onClose,
  onRetry,
}) => {
  const [copied, setCopied] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const [soundBlocked, setSoundBlocked] = useState(localStorage.getItem('browser_sound_blocked') === 'true');
  const browserInfo = getBrowserInfo();
  const settingsUrl = browserInfo.settingsUrl;

  useEffect(() => {
    if (open) {
      setNotificationPermission(getNotificationPermission());
      setSoundBlocked(localStorage.getItem('browser_sound_blocked') === 'true');
    }
  }, [open]);

  const handleCopySettingsUrl = async () => {
    try {
      await navigator.clipboard.writeText(settingsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: select text in input field
      const input = document.getElementById('settings-url-input') as HTMLInputElement;
      if (input) {
        input.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleOpenSettings = () => {
    // ✅ ENHANCED: Multiple methods to open Brave settings
    // Note: Browsers block brave:// URLs from JavaScript for security, so we need workarounds
    
    // Method 1: Try creating a temporary link and clicking it (most reliable)
    try {
      const link = document.createElement('a');
      link.href = settingsUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Try to click it
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      // If we get here, the click was attempted
      console.log('[BraveModal] Attempted to open settings via link click');
      
      // Show a helpful message
      toast.info('If settings did not open, please copy the URL below and paste it in your address bar', {
        autoClose: 5000,
      });
    } catch (e) {
      console.warn('[BraveModal] Link method failed, trying alternative:', e);
      
      // Method 2: Try window.open
      try {
        const newWindow = window.open(settingsUrl, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          throw new Error('Window blocked');
        }
      } catch (e2) {
        console.warn('[BraveModal] Window.open failed, using copy fallback:', e2);
        // Method 3: Fallback to copy
        handleCopySettingsUrl();
        toast.info('Settings URL copied! Please paste it in your address bar and press Enter', {
          autoClose: 5000,
        });
      }
    }
  };

  const getSteps = () => {
    if (browserInfo.name === 'Brave') {
      return [
        {
          label: `Open ${browserInfo.name} Settings`,
          description: 'Click the button below to open browser settings. If it doesn\'t open, copy the settings URL and paste it in your address bar.',
          action: handleOpenSettings,
          buttonText: `Open ${browserInfo.name} Settings`,
        },
        {
          label: 'Enable Push Messaging',
          description: 'In the Privacy and security settings, find "Use Google services for push messaging" and toggle it ON (purple/enabled)',
          action: null,
          buttonText: null,
        },
        {
          label: 'Refresh This Page',
          description: 'After enabling the setting, come back to this page and click the refresh button below',
          action: () => {
            if (onRetry) {
              onRetry();
            } else {
              window.location.reload();
            }
          },
          buttonText: 'Refresh Page',
        },
      ];
    } else if (browserInfo.name === 'Chrome' || browserInfo.name === 'Edge') {
      return [
        {
          label: `Open ${browserInfo.name} Settings`,
          description: 'Click the button below to open browser settings. If it doesn\'t open, copy the settings URL and paste it in your address bar.',
          action: handleOpenSettings,
          buttonText: `Open ${browserInfo.name} Settings`,
        },
        {
          label: 'Enable Notifications and Sound',
          description: `In ${browserInfo.name} settings, go to "Privacy and security" → "Site settings", ensure "Notifications" are allowed and "Sound" is allowed for this site.`,
          action: null,
          buttonText: null,
        },
        {
          label: 'Refresh This Page',
          description: 'After enabling notifications, come back to this page and click the refresh button below',
          action: () => {
            if (onRetry) {
              onRetry();
            } else {
              window.location.reload();
            }
          },
          buttonText: 'Refresh Page',
        },
      ];
    } else if (browserInfo.name === 'Firefox') {
      return [
        {
          label: `Open ${browserInfo.name} Settings`,
          description: 'Click the button below to open browser settings. If it doesn\'t open, copy the settings URL and paste it in your address bar.',
          action: handleOpenSettings,
          buttonText: `Open ${browserInfo.name} Settings`,
        },
        {
          label: 'Enable Notifications and Sound',
          description: 'In Firefox settings, go to "Privacy & Security" → "Permissions" → "Notifications" and allow this site, and ensure tab/site sound is not muted.',
          action: null,
          buttonText: null,
        },
        {
          label: 'Refresh This Page',
          description: 'After enabling notifications, come back to this page and click the refresh button below',
          action: () => {
            if (onRetry) {
              onRetry();
            } else {
              window.location.reload();
            }
          },
          buttonText: 'Refresh Page',
        },
      ];
    } else {
      return [
        {
          label: 'Enable Notifications and Sound',
          description: `Please enable push notifications and site sound in your ${browserInfo.name} browser settings for this site.`,
          action: null,
          buttonText: null,
        },
        {
          label: 'Refresh This Page',
          description: 'After enabling notifications, come back to this page and click the refresh button below',
          action: () => {
            if (onRetry) {
              onRetry();
            } else {
              window.location.reload();
            }
          },
          buttonText: 'Refresh Page',
        },
      ];
    }
  };

  const steps = getSteps();

  const handleRequestNotificationPermission = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        setNotificationPermission('unsupported');
        toast.info('Notifications are not supported in this browser.');
        return;
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Notifications enabled for this site');
      } else if (permission === 'denied') {
        toast.error('Notifications are blocked. Please allow from browser/site settings.');
      } else {
        toast.info('Notification permission is still pending/default.');
      }
    } catch (error) {
      console.error('[BraveModal] Failed to request notification permission:', error);
      toast.error('Unable to request notification permission automatically.');
    }
  };

  const handleSoundEnabled = () => {
    localStorage.removeItem('browser_sound_blocked');
    setSoundBlocked(false);
    toast.success('Sound status updated. You can now refresh and test order sound.');
  };

  const handleClose = () => {
    // Store dismissal in localStorage so it doesn't show again immediately
    localStorage.setItem('push_notification_modal_dismissed', Date.now().toString());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#fff3cd',
          borderBottom: '1px solid #ffc107',
        }}
      >
        <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        <Typography variant="h6" component="div" sx={{ flex: 1, fontWeight: 'bold' }}>
          {browserInfo.icon} {browserInfo.name} Browser: Enable Notifications and Sound
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: '#000',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> To receive order alerts in {browserInfo.name} browser, you need to enable
            notifications and allow site sound in browser settings. This is a one-time setup.
            {browserInfo.name === 'Brave' && ' For Brave, specifically enable "Use Google services for push messaging".'}
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Quick fix from address bar:</strong> click the lock icon near URL, set <strong>Notifications = Allow</strong> and <strong>Sound = Allow</strong>, then refresh.
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
          Follow these steps (all are visible below):
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {steps.map((step, index) => (
            <Box key={step.label} sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {index + 1}. {step.label}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {step.description}
              </Typography>
              {step.buttonText && (
                <Button
                  variant="contained"
                  startIcon={index === 0 ? <LaunchIcon /> : index === steps.length - 1 ? <RefreshIcon /> : undefined}
                  onClick={step.action || undefined}
                  color={index === 0 ? 'primary' : 'success'}
                >
                  {step.buttonText}
                </Button>
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, p: 2, border: '1px dashed #bdbdbd', borderRadius: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Verify and enable directly
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Current status: Notifications <strong>{notificationPermission}</strong>, Sound Blocked Flag <strong>{String(soundBlocked)}</strong>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={handleRequestNotificationPermission}>
              Request Notification Permission
            </Button>
            <Button variant="outlined" onClick={handleSoundEnabled}>
              I Enabled Site Sound
            </Button>
            <Button variant="contained" color="success" startIcon={<RefreshIcon />} onClick={onRetry}>
              Recheck and Refresh
            </Button>
          </Box>
        </Box>

        {settingsUrl && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
              📍 Alternative: Copy Settings URL
            </Typography>
            <TextField
              id="settings-url-input"
              fullWidth
              value={settingsUrl}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopySettingsUrl}
                      edge="end"
                      color={copied ? 'success' : 'default'}
                    >
                      {copied ? <CheckIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2 }}>
              Paste this URL in your {browserInfo.name} browser address bar and press Enter
            </Typography>
            
            {browserInfo.name === 'Brave' && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                  📍 Or Manual Navigation:
                </Typography>
                <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Click the <strong>three horizontal lines</strong> (☰) in the top-right corner of Brave</li>
                    <li>Select <strong>"Settings"</strong></li>
                    <li>Click <strong>"Privacy and security"</strong> in the left sidebar</li>
                    <li>Scroll down to find <strong>"Use Google services for push messaging"</strong></li>
                    <li>Toggle it <strong>ON</strong> (should turn purple/enabled)</li>
                    <li>Return to this page and refresh</li>
                  </ol>
                </Typography>
              </>
            )}
          </Box>
        )}
        
        <Snackbar
          open={copied}
          autoHideDuration={2000}
          message="Settings URL copied to clipboard!"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </DialogContent>

    </Dialog>
  );
};

export default BravePushSettingsModal;

